import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "./supabase";
import type { StudentRow } from "./types";

// ─────────────────────────────────────────────────────────────────────────────
// Auth context: single source of truth for session + the linked `students` row.
//
// Admin gating (Day 2 decision): we gate on the `is_admin` boolean on the
// student's `students` row (added in 0001_init.sql). If the student row hasn't
// been provisioned yet (e.g. a brand-new magic-link user before the Stripe
// webhook links them), we ALSO honor an env allowlist VITE_ADMIN_EMAILS
// (comma-separated) so Scott can reach /admin during the build before the
// enrollment/webhook plumbing exists. The DB flag is authoritative once present.
// ─────────────────────────────────────────────────────────────────────────────

interface AuthState {
  loading: boolean;
  session: Session | null;
  user: User | null;
  student: StudentRow | null;
  isAdmin: boolean;
  signOut: () => Promise<void>;
  /** Re-fetch the linked students row (after profile changes). */
  refreshStudent: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

function envAdminEmails(): string[] {
  const raw = import.meta.env.VITE_ADMIN_EMAILS ?? "";
  return raw
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
}

async function fetchStudent(user: User | null): Promise<StudentRow | null> {
  if (!user) return null;
  // Look up the students row linked to this auth user. Under RLS the student
  // can read their own row (policy students_self_select). May be null if the
  // account isn't provisioned/linked yet.
  const { data, error } = await supabase
    .from("students")
    .select(
      "id, auth_user_id, email, full_name, cert_number, aircraft_reg, is_admin"
    )
    .eq("auth_user_id", user.id)
    .maybeSingle();
  if (error) {
    // eslint-disable-next-line no-console
    console.warn("[auth] could not load students row:", error.message);
    return null;
  }
  return (data as StudentRow | null) ?? null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const [student, setStudent] = useState<StudentRow | null>(null);

  useEffect(() => {
    let active = true;

    // Load (or clear) the linked students row for a session WITHOUT blocking the
    // auth callback. See the deadlock note on onAuthStateChange below.
    const loadStudentFor = (s: Session | null) => {
      void fetchStudent(s?.user ?? null).then((row) => {
        if (active) setStudent(row);
      });
    };

    // Initial session load (covers reload + the magic-link redirect, which
    // detectSessionInUrl will have already parsed into a session). This runs
    // AFTER getSession() resolves — i.e. the auth lock has been released — so it
    // is safe to await the students fetch here. We resolve `loading` only once
    // the linked row is known, so admin gating (which depends on it) doesn't
    // bounce a real admin to /app on a race.
    supabase.auth.getSession().then(async ({ data }) => {
      if (!active) return;
      setSession(data.session);
      const row = await fetchStudent(data.session?.user ?? null);
      if (!active) return;
      setStudent(row);
      setLoading(false);
    });

    // React to sign-in / sign-out / token refresh anywhere in the app.
    //
    // IMPORTANT: this callback is invoked by supabase-js WHILE it holds the
    // internal auth navigator-lock (during _recoverAndRefresh on boot). Any
    // supabase query made *and awaited* in here re-enters that lock and
    // deadlocks — the session never resolves and every gated route hangs on the
    // "Loading…" splash forever. So we update synchronous React state here and
    // kick the students fetch off the lock (loadStudentFor does not await),
    // letting the lock release first.
    const { data: sub } = supabase.auth.onAuthStateChange(
      (_event, newSession) => {
        if (!active) return;
        setSession(newSession);
        loadStudentFor(newSession);
      }
    );

    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const user = session?.user ?? null;

  const isAdmin = useMemo(() => {
    if (student?.is_admin) return true;
    const email = user?.email?.toLowerCase();
    return !!email && envAdminEmails().includes(email);
  }, [student, user]);

  const value: AuthState = {
    loading,
    session,
    user,
    student,
    isAdmin,
    signOut: async () => {
      await supabase.auth.signOut();
    },
    refreshStudent: async () => {
      setStudent(await fetchStudent(user));
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
